/**
 * Recently Viewed — La Tienda del Padel
 * Tracks recently viewed products in localStorage and renders them on the product page.
 * Stores product handles for easy fetching via /products/{handle}.js
 */
class RecentlyViewed {
  constructor() {
    this.storageKey = 'la-tienda-recently-viewed';
    this.maxItems = 6;
    this.container = document.querySelector('[data-recently-viewed]');
    this.grid = document.querySelector('[data-recently-viewed-grid]');
    this.currentHandle = this.container?.dataset.productHandle;

    if (!this.container || !this.currentHandle) return;

    this.trackProduct();
    this.renderProducts();
  }

  trackProduct() {
    let items = this.getItems();
    items = items.filter((handle) => handle !== this.currentHandle);
    items.unshift(this.currentHandle);
    items = items.slice(0, this.maxItems);
    localStorage.setItem(this.storageKey, JSON.stringify(items));
  }

  getItems() {
    try {
      return JSON.parse(localStorage.getItem(this.storageKey) || '[]');
    } catch {
      return [];
    }
  }

  async renderProducts() {
    let items = this.getItems();
    items = items.filter((handle) => handle !== this.currentHandle);

    // Container starts hidden via Liquid; only unhide once we have cards to show.
    if (items.length === 0) return;

    const products = await this.fetchProducts(items);
    if (products.length === 0) return;
    if (!this.grid) return;

    this.grid.innerHTML = products.map((p) => this.renderCard(p)).join('');
    this.container.hidden = false;
  }

  async fetchProducts(handles) {
    const products = [];
    const deadHandles = [];

    for (const handle of handles) {
      try {
        const response = await fetch(`/products/${handle}.js`);
        if (response.ok) {
          const product = await response.json();
          products.push(product);
        } else if (response.status === 404) {
          // Product was deleted/archived — prune from localStorage so we
          // don't spam the console with 404s on every PDP visit.
          deadHandles.push(handle);
        }
      } catch {
        // Network errors are transient — don't prune, just skip this round.
      }
    }

    if (deadHandles.length > 0) {
      const fresh = this.getItems().filter((h) => !deadHandles.includes(h));
      localStorage.setItem(this.storageKey, JSON.stringify(fresh));
    }

    return products;
  }

  renderCard(product) {
    const image = product.featured_image
      ? `<img
          class="product-card__image product-card__image--primary"
          src="${product.featured_image}"
          alt="${this.escapeHtml(product.title)}"
          loading="lazy"
          width="400"
          height="400"
        >`
      : '<div class="product-card__image-placeholder"></div>';

    const price =
      product.compare_at_price && product.compare_at_price > product.price
        ? `<div class="price">
            <s class="price__compare">${this.formatMoney(product.compare_at_price)}</s>
            <span class="price__sale">${this.formatMoney(product.price)}</span>
          </div>`
        : `<div class="price">
            <span class="price__regular">${this.formatMoney(product.price)}</span>
          </div>`;

    return `
      <div class="product-card">
        <div class="product-card__image-wrapper">
          <a href="${product.url}" class="product-card__image-link" aria-label="${this.escapeHtml(product.title)}">
            ${image}
          </a>
        </div>
        <div class="product-card__content">
          <h3 class="product-card__title">
            <a href="${product.url}">${this.escapeHtml(product.title)}</a>
          </h3>
          <div class="product-card__price">${price}</div>
          ${product.vendor ? `<p class="product-card__vendor">${this.escapeHtml(product.vendor)}</p>` : ''}
        </div>
      </div>
    `;
  }

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    const formatted = parseFloat(amount).toString();
    return `EGP ${formatted}`;
  }

  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}

document.addEventListener('DOMContentLoaded', () => new RecentlyViewed());
